// Authentication utility functions

// Token validation
export function isTokenValid(token: string | null): boolean {
  if (!token) return false
  
  try {
    // Basic JWT structure validation
    const parts = token.split('.')
    if (parts.length !== 3) return false
    
    // Check if token is expired (basic check)
    const payload = JSON.parse(atob(parts[1]))
    const currentTime = Math.floor(Date.now() / 1000)
    
    if (payload.exp && payload.exp < currentTime) {
      return false
    }
    
    return true
  } catch {
    return false
  }
}

// Token expiration time
export function getTokenExpirationTime(token: string): number | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    
    const payload = JSON.parse(atob(parts[1]))
    return payload.exp || null
  } catch {
    return null
  }
}

// Time until token expires
export function getTimeUntilExpiration(token: string): number | null {
  const expirationTime = getTokenExpirationTime(token)
  if (!expirationTime) return null
  
  const currentTime = Math.floor(Date.now() / 1000)
  return Math.max(0, expirationTime - currentTime)
}

// Check if token needs refresh (within 5 minutes)
export function shouldRefreshToken(token: string): boolean {
  const timeUntilExpiration = getTimeUntilExpiration(token)
  if (timeUntilExpiration === null) return true
  
  // Refresh if token expires within 5 minutes
  return timeUntilExpiration < 300
}

// User role checking
export function hasRole(user: any, role: string): boolean {
  if (!user || !user.role) return false
  return user.role === role
}

export function isOwner(user: any): boolean {
  return hasRole(user, 'owner')
}

export function isMember(user: any): boolean {
  return hasRole(user, 'member')
}

// Permission checking
export function canEditTrip(trip: any, user: any): boolean {
  if (!trip || !user) return false
  
  // Trip creator can always edit
  if (trip.created_by === user.id) return true
  
  // Trip owners can edit
  if (trip.members?.some((member: any) => 
    member.user_id === user.id && member.role === 'owner'
  )) {
    return true
  }
  
  return false
}

export function canDeleteTrip(trip: any, user: any): boolean {
  if (!trip || !user) return false
  
  // Only trip creator can delete
  return trip.created_by === user.id
}

export function canManageMembers(trip: any, user: any): boolean {
  if (!trip || !user) return false
  
  // Trip creator can always manage members
  if (trip.created_by === user.id) return true
  
  // Trip owners can manage members
  if (trip.members?.some((member: any) => 
    member.user_id === user.id && member.role === 'owner'
  )) {
    return true
  }
  
  return false
}

// Session storage helpers (now using Supabase's built-in session management)
export function getStoredAuthData(): { token: string | null; user: any | null } {
  // This function is deprecated - use Supabase's session management instead
  // Keeping for backward compatibility but should not be used
  return { token: null, user: null }
}

export function clearStoredAuthData(): void {
  // This function is deprecated - Supabase handles session cleanup automatically
  // Keeping for backward compatibility but should not be used
  try {
    localStorage.removeItem('sitrepp_access_token')
    localStorage.removeItem('sitrepp_refresh_token')
    localStorage.removeItem('sitrepp_user_profile')
  } catch {
    // Ignore errors when clearing storage
  }
}

// Route persistence helpers
//
// `last_pathname` records where an authenticated user was so we can send them
// back there after auth-flow detours. Two classes of paths must never be stored
// or restored:
//   - Auth-flow pages (login/signup/etc) — they are the detour, not the target.
//   - Public marketing/informational pages (about/contact/legal/docs) — landing
//     on these for an authed user creates a redirect trap (e.g. visiting /about
//     once would bounce every later visit to / or /login back to /about).
const AUTH_FLOW_PREFIXES = ['/login', '/signup', '/forgot-password', '/reset-password']
const PUBLIC_MARKETING_PREFIXES = ['/about', '/contact', '/terms', '/privacy', '/docs']

// True when a path is a meaningful destination worth persisting/restoring.
export function isRestorablePath(path: string | null | undefined): path is string {
  if (!path || path === '/') return false
  return ![...AUTH_FLOW_PREFIXES, ...PUBLIC_MARKETING_PREFIXES].some((prefix) =>
    path.startsWith(prefix)
  )
}

// Redirect helpers
export function getRedirectPath(user: any): string {
  if (!user) return '/'
  
  // Check if user has any trips
  // This would typically come from user preferences or recent activity
  return '/dashboard'
}

export function getLoginRedirectPath(): string {
  const currentPath = window.location.pathname
  const publicPaths = ['/', '/login', '/signup']
  
  if (publicPaths.includes(currentPath)) {
    return '/dashboard'
  }
  
  return currentPath
}
